import { Component, Input, ViewChild } from '@angular/core'
import { NgForm } from '@angular/forms'
import { NgbActiveModal, NgbDateAdapter, NgbDateNativeAdapter, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { LocalStorage } from 'ngx-store'
import { MediaFile, MediaFileBundle, MediaLibraryShareRequest, MediaRootDetail, MediaRootPermission, OneTimeAccessToken } from 'elements-sdk'

import { APIService } from 'api/module'
import { PickerService } from 'common/util.pickers/module'
import { MessageBoxService, NotifyService } from 'common/util/module'
import { IdentityService } from 'main/services/identity.service'
import { ParametersService } from 'main/services/parameters.service'

import { SharingLinksReadyModalComponent } from './sharingLinksReadyModal.component'

interface HistoryEntry {
  userId?: number,
  email?: string,
  permission: Partial<MediaRootPermission>,
}

@Component({
  templateUrl: './sharingCreateLinkModal.component.pug',
  providers: [{ provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }],
})
export class SharingCreateLinkModalComponent {
  @Input() bundles: MediaFileBundle[]
  @Input() directories: MediaFile[]
  @Input() permission: MediaRootPermission
  @Input() root: MediaRootDetail

  @ViewChild(NgForm, { 'static': false }) form?: NgForm

  loading = false
  links = null
  recipients: any[] = [null]
  requestedPermissions: Partial<MediaRootPermission> = { allowRead: true }
  advancedPermissionsSelected = false
  expiry: Date = new Date(Date.now() + (30 * 24 * 3600 * 1000))
  minExpiry = new Date(Date.now() + 24 * 3600 * 1000)
  viewLimit = 0
  linkType?: string
  password?: string

  sectionsExpanded: Record<string, boolean> = {}

  @LocalStorage('shareHistory') history: HistoryEntry[] = []

  constructor (
    private ngbModal: NgbModal,
    private notify: NotifyService,
    private identity: IdentityService,
    private api: APIService,
    private translate: TranslateService,
    private picker: PickerService,
    private messageBox: MessageBoxService,
    private parameters: ParametersService,
    private modalInstance: NgbActiveModal,
  ) {
    if (parameters.values.mediaMaxLinkViews) {
      this.viewLimit = parameters.values.mediaMaxLinkViews
      this.sectionsExpanded.viewLimit = true
    }

    if (parameters.values.mediaRequireLinkPassword) {
      this.sectionsExpanded.password = true
    }
  }

  arePermissionsAllowed () {
    return !this.linkType?.startsWith('download-')
  }

  isAnonymous () {
    return this.recipients.every(x => !x)
  }

  isLinkTypeAllowed () {
    return !this.directories.length && this.bundles.length === 1
  }

  ngOnInit () {
    if (this.directories.length === 0) {
      this.permission = {
        ...this.permission,
        allowUpload: false,
      }
    }

    if (this.root.shareLinkDuration) {
      this.expiry = new Date(Date.now() + (+this.root.shareLinkDuration * 24 * 3600 * 1000))
    }
  }

  async addUser () {
    const users = this.api.main.getAllUsers({ includeEffectivePermissions: true }).then(u => u
      .filter(x => !this.recipients.includes(x))
      .filter(x => x.id !== this.identity.user?.id)
      .filter(x => x.effectivePermissions.includes('media:access') || x.username === 'root')
      .filter(x => x.isEnabled),
    )

    const pickedObjects = await this.picker.pickUsersAndGroups(users).catch(() => [])
    for (const item of pickedObjects) {
      if ('username' in item) {
        if (item.username !== 'root' && !item.effectivePermissions.includes('media:access')) {
          this.notify.warning(this.translate.instant('$user doesn\'t have access to Media Library').replace('$user', item.displayName))
          continue
        }
        this.addSingleUser(item)
      } else {
        this.api.main.getGroup({ id: item.id }).then(group => {
          const members = group.members!.filter(x => x.username === 'root' || x.effectivePermissions?.includes('media:access'))
          if (!members.length) {
            this.notify.warning(this.translate.instant(
              'Nobody in {group} has access to Media Library',
              { group: group.name },
            ))
            return
          }
          for (const member of members) {
            this.addSingleUser(member)
          }
        })
      }
    }
  }

  addSingleUser (user) {
    this.recipients = this.recipients.filter(x => x)
    if (!this.recipients.some(x => x.id === user.id)) {
      this.recipients.push(user)
    }
  }

  async addEmail () {
    const msg = await this.messageBox.prompt({ prompt: this.translate.instant('One or more e-mails, comma-separated'), inputType: 'text' }).catch(() => null)
    if (!msg?.value) {
      return
    }
    const emails = msg.value.split(',').map(s => s.trim())
    for (const email of emails) {
      if (email.length && !this.recipients.includes(email)) {
        if (email === this.identity.user?.email) {
          this.notify.warning(this.translate.instant('Cannot add yourself to the recipient list'))
        } else if (!this.recipients.includes(email)) {
          this.recipients.push(email)
        }
      }
    }
    this.recipients = this.recipients.filter(x => x)
  }

  removeRecipient (recipient) {
    this.recipients = this.recipients.filter(x => x !== recipient)
    if (!this.recipients.length) {
      this.recipients = [null]
    }
  }

  cancel () {
    this.modalInstance.dismiss()
  }

  async shareAnonymously () {
    this.recipients = [null]
    await this.create()
  }

  create = async () => {
    this.expiry.setHours(0)
    this.expiry.setMinutes(0)
    this.expiry.setSeconds(0)
    this.loading = true

    try {
      const links: { recipient: string, token: OneTimeAccessToken }[] = []
      for (const recipient of this.recipients) {
        const request: MediaLibraryShareRequest = {
          bundles: this.bundles.map(x => x.id),
          directories: this.directories.map(x => x.id),
          expires: this.expiry,
          viewLimit: this.viewLimit || null,
          permissions: this.requestedPermissions,
          linkType: this.linkType,
          password: this.password,
        }

        if (recipient) {
          if (recipient.id) {
            request.user = recipient.id
          } else {
            request.email = recipient
          }
        }

        const token = await this.api.mediaLibrary.shareMediaLibraryObjects({
          mediaLibraryShareRequest: request,
        })
        links.push({
          recipient,
          token,
        })
        this.history.push({
          userId: request.user,
          email: request.email,
          permission: { ...request.permissions },
        })
      }

      const m = this.ngbModal.open(
        SharingLinksReadyModalComponent,
        { size: 'transparent' },
      )
      m.componentInstance.links = links
      m.result.catch(() => null)

      this.modalInstance.close()
    } catch (error) {
      this.notify.errorFromResponse(this.translate.instant('Sharing failed'), error)
      this.loading = false
    }
  }

  isValid () {
    return (this.parameters.values.mediaAllowAnonymousLinks || !this.isAnonymous()) &&
      (!this.parameters.values.mediaRequireLinkPassword || this.password) &&
      Object.keys(this.requestedPermissions).some(k => this.requestedPermissions[k])
  }
}
